/**
 * MessageInput Component
 *
 * Multi-line text input for message analysis.
 * Features:
 * - Character counter with tiered color feedback (max 500 chars)
 * - Auto-grow height
 * - Clear button with haptic feedback
 * - Animated focus state (border color and shadow)
 * - Reduced motion support
 * - Enhanced accessibility (WCAG AA)
 * - Y2K Phase 2 (P1): CRT static/glitch effects during analysis
 */
import {
  View,
  TextInput,
  Text,
  AccessibilityInfo,
  Platform,
  TouchableOpacity,
} from "react-native";
import { useState, useEffect, useRef } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Ionicons from '@expo/vector-icons/Ionicons';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  interpolateColor,
  withRepeat,
  withSequence,
  Easing,
} from "react-native-reanimated";
import { useTranslation } from "@dramadetector/shared";
import { captureEvent } from "../lib/analytics";
import { useAutoGrowInput } from "../hooks/useAutoGrowInput";
import { PrivacyBadge } from "./privacy/PrivacyBadge";

/**
 * FontSizeControls - Accessibility controls for "authoring your narrative"
 * Extracted to reduce duplication and improve readability
 */
interface FontSizeControlsProps {
  textSize: number;
  disabled: boolean;
  minSize: number;
  maxSize: number;
  onDecrease: () => void;
  onIncrease: () => void;
}

function FontSizeControls({
  textSize,
  disabled,
  minSize,
  maxSize,
  onDecrease,
  onIncrease,
}: FontSizeControlsProps): React.JSX.Element {
  const isAtMin = textSize <= minSize;
  const isAtMax = textSize >= maxSize;

  const triggerHaptic = (): void => {
    if (Platform.OS === 'web') return;
    import('expo-haptics').then(({ impactAsync, ImpactFeedbackStyle }) => {
      impactAsync(ImpactFeedbackStyle.Light);
    });
  };

  const handleDecrease = (): void => {
    onDecrease();
    triggerHaptic();
  };

  const handleIncrease = (): void => {
    onIncrease();
    triggerHaptic();
  };

  const buttonStyle = (isDisabled: boolean) => ({
    padding: 14, // Phase 3: WCAG AAA touch targets (44×44px minimum)
    borderRadius: 8,
    opacity: isDisabled ? 0.3 : 1,
  });

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <TouchableOpacity
        onPress={handleDecrease}
        disabled={disabled || isAtMin}
        style={buttonStyle(isAtMin)}
        accessibilityLabel="Decrease font size"
        accessibilityRole="button"
        accessibilityHint="Makes text smaller for typing your message"
      >
        <Ionicons
          name="text-outline"
          size={16}
          color={isAtMin ? '#4b5563' : '#9ca3af'}
        />
      </TouchableOpacity>

      <Text style={{
        fontSize: 11,
        color: '#6b7280',
        fontFamily: Platform.select({
          ios: 'Courier New',
          android: 'monospace',
          web: 'Courier New',
        }),
        minWidth: 24,
        textAlign: 'center',
      }}>
        {textSize}
      </Text>

      <TouchableOpacity
        onPress={handleIncrease}
        disabled={disabled || isAtMax}
        style={buttonStyle(isAtMax)}
        accessibilityLabel="Increase font size"
        accessibilityRole="button"
        accessibilityHint="Makes text larger for typing your message"
      >
        <Ionicons
          name="text-outline"
          size={22}
          color={isAtMax ? '#4b5563' : '#9ca3af'}
        />
      </TouchableOpacity>
    </View>
  );
}

interface MessageInputProps {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  error?: string; // For WCAG accessibilityState
  onPaste?: (pastedText: string) => void; // Callback for paste detection (mobile demand signal)
  isPending?: boolean; // Triggers CRT static overlay during analysis
  isKeyboardVisible?: boolean; // Apply scroll padding when keyboard visible
  onHeightChange?: (height: number) => void; // Height-based docking threshold
  isAnonymous?: boolean; // Controls trial prompts/turnstile UI (does NOT affect input height - always 168px)
  onAnalyze?: () => void; // Inline analyze button callback (keyboard active)
  isAnalyzeDisabled?: boolean; // Disable analyze button state
}

export function MessageInput({
  value,
  onChangeText,
  placeholder,
  maxLength = 500,
  disabled = false,
  error: _error,
  onPaste,
  isPending = false,
  isKeyboardVisible = false,
  onHeightChange,
  isAnonymous = false,
  onAnalyze,
  isAnalyzeDisabled = false,
}: MessageInputProps) {
  const { t } = useTranslation('home');
  const [isFocused, setIsFocused] = useState(false);
  const [isReduceMotionEnabled, setReduceMotionEnabled] = useState(false);
  const [previousLength, setPreviousLength] = useState(0);

  // Fixed-height input hook (Phase 4: Simplified to fixed height, no auto-grow)
  // Note: handleContentSizeChange, handleFocus, handleBlur are no-ops in fixed-height mode
  const inputRef = useRef<TextInput>(null);
  const { height, MIN_HEIGHT, MAX_HEIGHT } = useAutoGrowInput(onHeightChange);

  // Font size control - "authoring your narrative" accessibility
  // Default 16px aligns with screenwriter aesthetic and iOS requirements
  const [textSize, setTextSize] = useState(16);
  const [isFontSizeLoaded, setIsFontSizeLoaded] = useState(false);

  const characterCount = value.length;

  // Cross-platform storage helpers for font size persistence
  const FONT_SIZE_KEY = 'messageInputFontSize';
  const MIN_FONT_SIZE = 16; // Phase 3: iOS auto-zoom prevention (must be ≥16px)
  const MAX_FONT_SIZE = 22;

  const getStoredFontSize = async (): Promise<number | null> => {
    const raw = Platform.OS === 'web'
      ? localStorage.getItem(FONT_SIZE_KEY)
      : await AsyncStorage.getItem(FONT_SIZE_KEY);

    if (!raw) return null;

    const size = parseInt(raw, 10);
    return size >= MIN_FONT_SIZE && size <= MAX_FONT_SIZE ? size : null;
  };

  const setStoredFontSize = async (size: number): Promise<void> => {
    const value = size.toString();
    if (Platform.OS === 'web') {
      localStorage.setItem(FONT_SIZE_KEY, value);
    } else {
      await AsyncStorage.setItem(FONT_SIZE_KEY, value);
    }
  };

  // Load saved font size preference on mount (user's "writing voice")
  useEffect(() => {
    getStoredFontSize()
      .then((size) => { if (size) setTextSize(size); })
      .catch(() => { /* Silent fail - use default 16px */ })
      .finally(() => setIsFontSizeLoaded(true));
  }, []);

  // Save font size preference when it changes (persist user's "writing voice")
  useEffect(() => {
    if (!isFontSizeLoaded) return;

    setStoredFontSize(textSize)
      .then(() => {
        captureEvent('message_input_font_size_changed', {
          font_size: textSize,
          platform: Platform.OS as 'ios' | 'android' | 'web',
        });
      })
      .catch(() => { /* Silent fail - don't interrupt user's writing flow */ });
  }, [textSize, isFontSizeLoaded]);

  // Y2K Phase 2 (P1): CRT static pulse animation
  const staticOpacity = useSharedValue(0);

  useEffect(() => {
    if (isPending && !isReduceMotionEnabled) {
      // Pulse static overlay: 0.15 → 0.25 → 0.15 (1s cycle)
      staticOpacity.value = withRepeat(
        withSequence(
          withTiming(0.25, { duration: 500, easing: Easing.inOut(Easing.ease) }),
          withTiming(0.15, { duration: 500, easing: Easing.inOut(Easing.ease) })
        ),
        -1, // Infinite repeat during analysis
        false
      );

      // Track CRT static effect (Y2K Phase 2 P1)
      // y2k_visual_effect_shown retired (#797)
    } else {
      // Fade out static when analysis completes
      staticOpacity.value = withTiming(0, { duration: 300 });
    }

    // Cleanup on unmount
    return () => {
      staticOpacity.value = 0;
    };
  }, [isPending, isReduceMotionEnabled]);

  const staticOverlayStyle = useAnimatedStyle(() => ({
    opacity: staticOpacity.value,
  }));

  // NEW: Detect clipboard paste (mobile demand signal)
  // When text changes suddenly by a large amount, it's likely a paste event
  const handleTextChange = (text: string) => {
    const textLengthDelta = text.length - previousLength;

    // Detect paste: large text change (>10 chars) or very fast multi-char change
    // This heuristic works because typing is typically 1-3 chars at a time
    if (textLengthDelta > 10 && onPaste) {
      // Extract the pasted portion (approximation)
      const pastedText = text.substring(previousLength);
      onPaste(pastedText);
    }

    // Track when user hits character limit (tier upsell opportunity)
    // If new text length equals maxLength and delta > 1, user likely tried to paste more
    if (text.length === maxLength && textLengthDelta > 1) {
      captureEvent('Analysis_CharacterLimitExceeded', {
        tier: 'anonymous', // Will be overridden by parent context if available
        character_limit: maxLength,
        character_count: text.length,
        excess_characters: 0, // TextInput truncates, we don't know exact excess
        platform: Platform.OS as 'ios' | 'android' | 'web',
        can_upgrade: true, // Assume upgradeable from MessageInput context
      });
    }

    setPreviousLength(text.length);
    onChangeText(text);
  };

  // 1. Reduced Motion Support: Check accessibility settings on mount and listen for changes.
  useEffect(() => {
    const checkReduceMotion = async () => {
      setReduceMotionEnabled(await AccessibilityInfo.isReduceMotionEnabled());
    };

    checkReduceMotion();

    const subscription = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      (enabled) => {
        setReduceMotionEnabled(enabled);
      },
    );

    return () => {
      subscription.remove();
    };
  }, []);

  // 2. Input Focus Animation: Use Reanimated for smooth transitions.
  const focusProgress = useSharedValue(0);

  useEffect(() => {
    const duration = isReduceMotionEnabled ? 0 : 200;
    focusProgress.value = withTiming(isFocused ? 1 : 0, { duration });
  }, [isFocused, isReduceMotionEnabled]);

  // Inner textarea wrapper style (animated focus border)
  const animatedInputWrapperStyle = useAnimatedStyle(() => {
    // Web: Snap to purple when focused (no interpolation - Reanimated plugin disabled for web)
    // Native: Smooth color interpolation
    const borderColor = Platform.OS === 'web'
      ? (focusProgress.value > 0.5 ? 'rgba(183, 148, 244, 0.5)' : 'rgba(255, 255, 255, 0.1)')
      : interpolateColor(
          focusProgress.value,
          [0, 1],
          ["rgba(255, 255, 255, 0.1)", "rgba(183, 148, 244, 0.5)"], // white/10 -> purple-400/50
        );

    // Animate shadow and elevation for a subtle lift effect on focus.
    const shadowOpacity = focusProgress.value * 0.05;
    const elevation = focusProgress.value * 3;

    // Platform-specific shadow handling
    if (Platform.OS === 'web') {
      // Web: Use CSS boxShadow (no deprecation warnings)
      return {
        borderColor,
        boxShadow: `0px 2px 4px rgba(0, 0, 0, ${shadowOpacity})`,
      };
    }

    // Native (iOS/Android): Use RN shadow properties
    return {
      borderColor,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowRadius: 4,
      shadowOpacity,
      elevation,
    };
  });



  return (
    <View className="w-full max-w-md mx-auto px-4">
      {/* Outer glass container - matches prototype glass-card with p-4 */}
      <Animated.View
        style={[
          Platform.OS === 'web' ? {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: 16,
            padding: 16,
            borderWidth: 1,
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderStyle: 'solid',
            position: 'relative',
            overflow: 'hidden',
          } as any : {
            borderRadius: 16,
            padding: 16,
            borderWidth: 1,
            borderColor: 'rgba(255, 255, 255, 0.1)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            position: 'relative',
            overflow: 'hidden',
          },
          disabled && { opacity: 0.6 },
        ]}
      >
        {/* Y2K Phase 2 (P1): CRT Static Overlay */}
        {isPending && (
          <Animated.View
            style={[
              staticOverlayStyle,
              {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#000000',
                pointerEvents: 'none', // Allow interaction with input below
              },
            ]}
            accessibilityElementsHidden={true}
          />
        )}

        {/* Textarea wrapper - animated border only (no background) */}
        <Animated.View
          style={[
            animatedInputWrapperStyle,
            {
              borderRadius: 12,
              borderWidth: 1,
              borderStyle: 'solid',
              position: 'relative',
            },
          ]}
        >
          <TextInput
            ref={inputRef}
            value={value}
            onChangeText={handleTextChange}
            placeholder={placeholder || t('message_input.placeholder')}
            placeholderTextColor="#6b7280"
            multiline
            maxLength={maxLength}
            editable={!disabled}
            onFocus={() => {
              setIsFocused(true);
              // Note: handleInputFocus removed (no-op in fixed-height mode)
            }}
            testID="message-input"
            onBlur={() => {
              setIsFocused(false);
              // Note: handleInputBlur removed (no-op in fixed-height mode)
            }}
            style={{
              fontSize: textSize,
              color: '#ffffff',
              lineHeight: textSize * 1.5, // Optimal readability (research: 1.5-1.6x)
              paddingHorizontal: 16,
              paddingVertical: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              textAlignVertical: "top",
              width: '100%',
              // Courier New for "screenwriter typing stories" aesthetic
              // Aligns with Narrator voice and creates drafting mindset
              fontFamily: Platform.select({
                ios: 'Courier New',
                android: 'monospace', // Android system monospace
                web: 'Courier New',
              }),
              ...(Platform.OS === 'web' ? {
                // Phase 1 & 2: iOS Safari viewport fix + fixed height
                // Phase 1 & 2: iOS Safari viewport fix + fixed height
                // Fixed 168px height (6 lines at 16px font), content scrolls internally
                height: height,
                resize: 'none',
                outline: 'none',
              } : {
                // Native: Fixed height (content scrolls internally when font size increases)
                height: height,
              }),
            } as any}
            accessibilityLabel={t('message_input.accessibility.input_label')}
            accessibilityHint={t('message_input.accessibility.input_hint')}
            accessibilityState={{ disabled }}
          />
        </Animated.View>

        {/* Toolbar - mt-3 matches prototype */}
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 12,
          paddingHorizontal: 4,
        }}>
          {/* Left: Character counter + Privacy badge */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <Text style={{
              fontSize: 12,
              color: characterCount >= maxLength * 0.95 ? '#dc2626' :
                     characterCount >= maxLength * 0.8 ? '#ea580c' :
                     '#6b7280',
              fontWeight: characterCount >= maxLength * 0.95 ? '700' :
                          characterCount >= maxLength * 0.8 ? '500' :
                          '400',
            }}>
              {characterCount} / {maxLength}
            </Text>
            <PrivacyBadge />
          </View>

          {/* Right: Controls group */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {/* Font size controls */}
            <FontSizeControls
              textSize={textSize}
              disabled={disabled}
              minSize={MIN_FONT_SIZE}
              maxSize={MAX_FONT_SIZE}
              onDecrease={() => setTextSize(Math.max(MIN_FONT_SIZE, textSize - 2))}
              onIncrease={() => setTextSize(Math.min(MAX_FONT_SIZE, textSize + 2))}
            />

            {/* Inline Analyze button - visible in both default and focus modes */}
            {onAnalyze && (
              <TouchableOpacity
                onPress={onAnalyze}
                disabled={isAnalyzeDisabled}
                // Mobile web fix: prevent textarea blur (keyboard dismissal) when tapping
                // the submit button. Without this, the focusout→layout shift race condition
                // causes the tap to miss the button entirely.
                {...(Platform.OS === 'web' ? { onMouseDown: (e: React.MouseEvent) => e.preventDefault() } : {})}
                testID="analyze-submit-button"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  justifyContent: 'center',
                  alignItems: 'center',
                  overflow: 'hidden',
                  opacity: isAnalyzeDisabled ? 0.5 : 1,
                }}
                accessibilityLabel={t('home.analyze_button')}
                accessibilityRole="button"
              >
                {/* Theatrical gradient background (matches FAB) */}
                <View style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  ...(Platform.OS === 'web' ? {
                    background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                  } as any : {}),
                }}>
                  {Platform.OS !== 'web' && (
                    <View style={{
                      flex: 1,
                      backgroundColor: '#ec4899', // Fallback for native (LinearGradient would need import)
                    }} />
                  )}
                </View>
                {/* Send icon - simple triangle/arrow */}
                <Ionicons name="send" size={20} color="#FFFFFF" />
              </TouchableOpacity>
            )}
          </View>
        </View>
      </Animated.View>
    </View>
  );
}
